import {Button, CircularProgress} from '@material-ui/core';
import React, {useState, useMemo, useCallback, useEffect} from 'react';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import ContractType from './components/ContractType';
import CropAndParties from './components/CropAndParties';
import Pricing from './components/Pricing';
import Delivery from './components/Delivery';
import ProductionPlan from './components/ProductionPlan';
import FundingTermSheet from './components/FundingTermSheet';
import {IGenericType, IUser} from 'constants/interfaces';
import {ISimpleProduceItem, TFarmField} from './models';
import {apiCREATEFARMPRODUCECONTRACT} from './utils/graph';
import {reduxToggleSidePanel} from 'appredux/actions';

type AddContractsProps = {
  data: {
    farmId: any;
    farmerId: any;
  };
};

enum Steps {
  ContractType = 'Contract Type',
  PartiesAndCrop = 'Parties and crop',
  Pricing = 'Pricing',
  DeliveryAndStorage = 'Delivery and storage',
  ProductionPlan = 'Production plan',
  FundingTermSheet = 'Funding term sheet',
}
const allSteps = [
  Steps.ContractType,
  Steps.PartiesAndCrop,
  Steps.Pricing,
  Steps.DeliveryAndStorage,
  Steps.ProductionPlan,
  Steps.FundingTermSheet,
];

const AddContracts = (props: AddContractsProps) => {
  const [activeStep, setActiveStep] = useState<Steps>(Steps.ContractType);
  const [contractType, setContractType] = useState<IGenericType>();
  const [includeFundingInfo, setIncludeFundingInfo] = useState<boolean>();
  const [buyer, setBuyer] = useState<IUser>();
  const [produce, setProduce] = useState<ISimpleProduceItem>();
  const [variety, setVariety] = useState<IGenericType>();
  const [fees, setFees] = useState<Array<{id: string; amount?: string}>>([]);
  const [price, setPrice] = useState<string>('');
  const [pricePer, setPricePer] = useState<string>('');
  const [deliveryTerm, setDeliveryTerm] = useState<IGenericType>();
  const [deliveryPreference, setDeliveryPreference] = useState<IGenericType>();
  const [farmFields, setFarmFields] = useState<Array<TFarmField>>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [supplyDate, setSupplyDate] = useState<Date | null>(null);
  const [hectares, setHectares] = useState<string>();
  const [estimatedYield, setEstimatedYield] = useState<string>();
  const [irrigation, setIrrigation] = useState<IGenericType>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const steps = useMemo(() => {
    if (includeFundingInfo) return allSteps;
    return allSteps.slice(0, 5);
  }, [includeFundingInfo]);
  const isLastStep = useMemo(
    () => steps.indexOf(activeStep) === steps.length - 1,
    [activeStep, steps],
  );

  const handleNextStep = useCallback(() => {
    const isLastStep = steps.indexOf(activeStep) === steps.length - 1;
    if (isLastStep) {
      //   setActiveStep(Steps.ContractType);
      onSubmit();
      return;
    }
    setActiveStep(steps[steps.indexOf(activeStep) + 1]);
  }, [activeStep, steps, isLastStep]);
  const handleBackStep = useCallback(() => {
    setActiveStep(steps[steps.indexOf(activeStep) - 1]);
  }, [activeStep, steps, isLastStep]);

  const onSubmit = useCallback(() => {
    if (
      !produce?.id ||
      !variety?.id ||
      !buyer?.id ||
      !irrigation?.id ||
      !startDate ||
      !supplyDate
    ) {
      return;
    }
    setIsLoading(true);
    apiCREATEFARMPRODUCECONTRACT({
      farmerId: props.data.farmerId,
      produceId: produce?.id,
      varietyId: variety?.id,
      buyerId: buyer?.id,
      fieldType: irrigation?.id,
      expectedPrice: Number(price),
      expectedPriceUnit: pricePer,
      contractTypeId: contractType?.id,
      deliveryPreferenceId: deliveryPreference?.id,
      deliveryTermsId: deliveryTerm?.id,
      hectaresPlanted: Number(hectares),
      estimatedHarvestQuantity: Number(estimatedYield),
      plantedDate: (startDate as any)?.toISOString(),
      estimatedSupplyDate: (supplyDate as any)?.toISOString(),
      ...(fees.length > 0 && {
        fees: fees.map((fee) => ({
          id: fee.id,
          amount: Number(fee.amount),
        })),
      }),
      ...(farmFields.length > 0 && {
        fields: farmFields.map((field) => field.id),
      }),
    })
      .then((res) => {
        if (res.success) {
          reduxToggleSidePanel(false, null, true);
        } else {
          alert(res.message);
          console.log(res?.message);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    startDate,
    supplyDate,
    fees,
    farmFields,
    irrigation,
    price,
    pricePer,
    contractType,
    deliveryPreference,
    deliveryTerm,
    produce,
    variety,
    buyer,
    hectares,
    estimatedYield,
    props?.data?.farmerId,
    props?.data?.farmId,
  ]);

  useEffect(() => {
    console.log({
      startDate,
      startDateFormatted: (startDate as any)?.format('DD/MM/YYYY'),
    });
  }, [startDate]);
  useEffect(() => {
    console.log({hectares, estimatedYield});
  }, [hectares, estimatedYield]);
  return (
    <div className="contracts-panel">
      <Stepper
        className="stepper-container"
        activeStep={steps.indexOf(activeStep)}
        variant="outlined"
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          padding: '0 20px',
          marginBottom: 30,
        }}
      >
        {steps.map((label) => {
          return (
            <Step key={label}>
              <StepLabel>
                <div style={{fontSize: 8}}>{label}</div>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <div className="panel-content-container">
        {activeStep === Steps.ContractType && (
          <ContractType
            contractType={contractType}
            includeFundingInfo={includeFundingInfo}
            onIncludeFundingInfo={(value) => setIncludeFundingInfo(value)}
            onContractType={(value) => setContractType(value)}
          />
        )}
        {activeStep === Steps.PartiesAndCrop && (
          <CropAndParties
            buyer={buyer}
            produce={produce}
            variety={variety}
            onBuyer={(value) => setBuyer(value)}
            onProduce={(value) => setProduce(value)}
            onVariety={(value) => setVariety(value)}
          />
        )}
        {activeStep === Steps.Pricing && (
          <Pricing
            fees={fees as any}
            onFees={setFees}
            pricePer={pricePer}
            onPricePer={setPricePer}
            price={price}
            onPrice={setPrice}
          />
        )}
        {activeStep === Steps.DeliveryAndStorage && (
          <Delivery
            deliveryTerm={deliveryTerm}
            deliveryPreference={deliveryPreference}
            onDeliveryTerm={setDeliveryTerm}
            onDeliveryPreference={setDeliveryPreference}
          />
        )}
        {activeStep === Steps.ProductionPlan && (
          <ProductionPlan
            farmId={props.data.farmId}
            includeFundingInfo={includeFundingInfo}
            hectares={hectares}
            estimatedYield={estimatedYield}
            irrigation={irrigation}
            startDate={startDate}
            supplyDate={supplyDate}
            farmFields={farmFields}
            onHectares={setHectares}
            onEstimatedYield={setEstimatedYield}
            onIrrigation={setIrrigation}
            onStartDate={setStartDate}
            onSupplyDate={setSupplyDate}
            onFarmFields={setFarmFields}
          />
        )}
        {activeStep === Steps.FundingTermSheet && <FundingTermSheet />}
      </div>
      <div className="panel-footer">
        {activeStep !== Steps.ContractType && (
          <Button
            variant="contained"
            size="medium"
            className="footer-button"
            onClick={handleBackStep}
          >
            <FontAwesomeIcon
              icon={faChevronLeft}
              style={{
                fontSize: '14px',
                marginRight: '10px',
              }}
            />
            Back
          </Button>
        )}
        <div style={{flex: 1}} />
        <Button
          variant="contained"
          size="medium"
          className="footer-button"
          onClick={isLastStep ? onSubmit : handleNextStep}
        >
          {isLastStep ? 'Submit' : 'Next'}
          {isLoading ? (
            <CircularProgress
              color={'inherit'}
              size={14}
              thickness={4}
              style={{
                marginLeft: '10px',
              }}
            />
          ) : (
            <FontAwesomeIcon
              icon={faChevronRight}
              style={{
                fontSize: '14px',
                marginLeft: '10px',
              }}
            />
          )}
        </Button>
      </div>
    </div>
  );
};

export default AddContracts;
